import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  Collapse,
  useTheme,
  alpha,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  VpnKey,
  Category,
  Business,
  Message,
  School,
  Info,
  Logout,
  Language,
  Handshake,
  Description,
  Gavel,
  Event,
  Folder,
  Mail,
  AccountBalance,
  LocationCity,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  ManageHistory,
  Dashboard as DashboardIcon,
  Settings,
  AdminPanelSettings,
  Person,
  AutoAwesome,
  Groups,
  PictureAsPdf,
  Collections,
  Place,
  Link as LinkIcon,
  Article,
} from '@mui/icons-material';

const DRAWER_WIDTH = 220;
const DRAWER_COLLAPSED = 56;
const TOPBAR_HEIGHT = 56;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [managementOpen, setManagementOpen] = useState(true);
  const [businessOpen, setBusinessOpen] = useState(true);
  const [communicationsOpen, setCommunicationsOpen] = useState(true);
  const [resourcesOpen, setResourcesOpen] = useState(true);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);

  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isRTL = i18n.language === 'ar';

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  const handleSignOut = async () => {
    setProfileMenuAnchor(null);
    await signOut();
    navigate('/login');
  };

  const handleProfileMenuOpen = (e: React.MouseEvent<HTMLElement>) => setProfileMenuAnchor(e.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchor(null);
  const handleProfileSettings = () => { setProfileMenuAnchor(null); navigate('/profile-settings'); };
  const handleAdminSettings = () => { setProfileMenuAnchor(null); navigate('/admin-users'); };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  const menuSections = [
    {
      title: t('sections.management'),
      key: 'management',
      open: managementOpen,
      setOpen: setManagementOpen,
      items: [
        { key: 'companies', label: t('nav.companies'), icon: <Business />, path: '/companies' },
        { key: 'partners', label: t('nav.partners'), icon: <Handshake />, path: '/partners' },
        { key: 'authSystem', label: t('nav.authSystem'), icon: <VpnKey />, path: '/auth-system' },
      ],
    },
    {
      title: t('sections.business'),
      key: 'business',
      open: businessOpen,
      setOpen: setBusinessOpen,
      items: [
        { key: 'classifications', label: t('nav.classifications'), icon: <Category />, path: '/classifications' },
        { key: 'contracts', label: t('nav.contracts'), icon: <Description />, path: '/contracts' },
        { key: 'tenders', label: t('nav.tenders'), icon: <Gavel />, path: '/tenders' },
        { key: 'events', label: t('nav.events'), icon: <Event />, path: '/events' },
        { key: 'training', label: t('nav.training'), icon: <School />, path: '/trainings' },
        { key: 'boardMeetings', label: t('nav.boardMeetings'), icon: <Groups />, path: '/board-meetings' },
        { key: 'boardAttachments', label: t('nav.boardAttachments'), icon: <PictureAsPdf />, path: '/board-attachments' },
      ],
    },
    {
      title: t('sections.communications'),
      key: 'communications',
      open: communicationsOpen,
      setOpen: setCommunicationsOpen,
      items: [
        { key: 'messages', label: t('nav.messages'), icon: <Message />, path: '/messages' },
        { key: 'news', label: t('nav.news'), icon: <Info />, path: '/news' },
        { key: 'gallery', label: t('nav.gallery'), icon: <Collections />, path: '/gallery' },
        { key: 'outwardLetters', label: t('nav.outwardLetters'), icon: <Mail />, path: '/outward-letters' },
      ],
    },
    {
      title: t('sections.resources'),
      key: 'resources',
      open: resourcesOpen,
      setOpen: setResourcesOpen,
      items: [
        { key: 'files', label: t('nav.files'), icon: <Folder />, path: '/files' },
        { key: 'socialFund', label: t('nav.socialFund'), icon: <AccountBalance />, path: '/fund-system' },
        { key: 'branches', label: t('nav.branches'), icon: <Place />, path: '/branches' },
        { key: 'relatedLinks', label: t('nav.relatedLinks'), icon: <LinkIcon />, path: '/related-links' },
        { key: 'infoPages', label: t('nav.infoPages'), icon: <Article />, path: '/info-pages' },
        { key: 'documentTemplates', label: t('nav.documentTemplates'), icon: <Description />, path: '/document-templates' },
        { key: 'activityLog', label: t('nav.activityLog'), icon: <ManageHistory />, path: '/activity' },
        { key: 'areasAndCities', label: t('nav.areasAndCities'), icon: <LocationCity />, path: '/areas-cities' },
        { key: 'adminUsers', label: t('nav.adminUsers'), icon: <AdminPanelSettings />, path: '/admin-users' },
        { key: 'settings', label: t('nav.settings') || 'Settings', icon: <Settings />, path: '/settings' },
      ],
    },
  ];

  const navItemSx = () => ({
    minHeight: 36,
    px: 1.5,
    justifyContent: drawerOpen ? 'flex-start' : 'center',
    borderRadius: 1.5,
    mb: 0.25,
    transition: 'background-color 0.15s',
    '&.Mui-selected': {
      bgcolor: alpha(theme.palette.primary.main, 0.1),
      color: 'primary.main',
      '& .MuiListItemIcon-root': { color: 'primary.main' },
      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.14) },
    },
    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
  });

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>

      {/* ── Brand header ──────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: TOPBAR_HEIGHT,
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          ...(drawerOpen
            ? { px: 2, gap: 1.5 }
            : { justifyContent: 'center' }
          ),
        }}
      >
        {drawerOpen ? (
          <>
            <Box
              component="img"
              src="/logo192.png"
              alt="PCU Logo"
              sx={{ width: 32, height: 32, borderRadius: 1, objectFit: 'contain', flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'text.primary' }}
              >
                {t('appName')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {t('adminPanel')}
              </Typography>
            </Box>
            <IconButton
              onClick={toggleDrawer}
              size="small"
              sx={{
                flexShrink: 0,
                display: { xs: 'none', sm: 'flex' },
                color: 'text.secondary',
                borderRadius: 1,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              {isRTL ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
            </IconButton>
          </>
        ) : (
          <Tooltip title={t('appName')} placement={isRTL ? 'left' : 'right'}>
            <IconButton
              onClick={toggleDrawer}
              size="small"
              sx={{
                display: { xs: 'none', sm: 'flex' },
                color: 'text.secondary',
                borderRadius: 1,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              {isRTL ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ── Dashboard ─────────────────────────────────────────── */}
      <List sx={{ pt: 1.5, pb: 0.5, px: drawerOpen ? 1 : 0.75 }}>
        <Tooltip title={!drawerOpen ? t('dashboard') : ''} placement={isRTL ? 'left' : 'right'}>
          <ListItemButton
            selected={location.pathname === '/'}
            onClick={() => navigate('/')}
            sx={navItemSx()}
          >
            <ListItemIcon sx={{ minWidth: drawerOpen ? 36 : 'auto', justifyContent: 'center' }}>
              <DashboardIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary={t('dashboard')}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: location.pathname === '/' ? 600 : 400 }}
              />
            )}
          </ListItemButton>
        </Tooltip>

        <Tooltip title={!drawerOpen ? t('nav.aiAgent') : ''} placement={isRTL ? 'left' : 'right'}>
          <ListItemButton
            selected={location.pathname === '/ai-agent'}
            onClick={() => navigate('/ai-agent')}
            sx={navItemSx()}
          >
            <ListItemIcon sx={{ minWidth: drawerOpen ? 36 : 'auto', justifyContent: 'center' }}>
              <AutoAwesome sx={{ fontSize: 20 }} />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary={t('nav.aiAgent')}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: location.pathname === '/ai-agent' ? 600 : 400 }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </List>

      <Divider sx={{ mx: drawerOpen ? 1.5 : 0.75 }} />

      {/* ── Nav sections ──────────────────────────────────────── */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', px: drawerOpen ? 1 : 0.75, py: 0.75 }}>
        {menuSections.map((section, idx) => (
          <Box key={section.key} sx={{ mb: drawerOpen ? 0.5 : 0 }}>
            {!drawerOpen && idx > 0 && (
              <Divider sx={{ my: 0.75, mx: 0.5 }} />
            )}

            {drawerOpen && (
              <ListItemButton
                onClick={() => section.setOpen(!section.open)}
                sx={{
                  px: 1.5,
                  py: 0.375,
                  borderRadius: 1,
                  mb: 0.25,
                  minHeight: 28,
                  '&:hover': { bgcolor: 'transparent' },
                }}
                disableRipple
              >
                <ListItemText
                  primary={section.title}
                  primaryTypographyProps={{
                    variant: 'caption',
                    fontWeight: 600,
                    color: 'text.disabled',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    fontSize: '0.6875rem',
                  }}
                />
                {section.open
                  ? <ExpandLess sx={{ fontSize: 14, color: 'text.disabled' }} />
                  : <ExpandMore sx={{ fontSize: 14, color: 'text.disabled' }} />
                }
              </ListItemButton>
            )}

            <Collapse in={drawerOpen ? section.open : true} timeout="auto">
              <List component="div" disablePadding>
                {section.items.map((item) => (
                  <Tooltip
                    key={item.key}
                    title={!drawerOpen ? item.label : ''}
                    placement={isRTL ? 'left' : 'right'}
                  >
                    <ListItemButton
                      selected={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                      sx={navItemSx()}
                    >
                      <ListItemIcon sx={{ minWidth: drawerOpen ? 36 : 'auto', justifyContent: 'center' }}>
                        {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                      </ListItemIcon>
                      {drawerOpen && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: location.pathname === item.path ? 600 : 400,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>

      {/* ── Profile ───────────────────────────────────────────── */}
      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 1 }}>
        <Box
          onClick={handleProfileMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: drawerOpen ? 'flex-start' : 'center',
            gap: 1,
            cursor: 'pointer',
            borderRadius: 1.5,
            p: 0.75,
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
          }}
        >
          <Avatar
            sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.8125rem', flexShrink: 0 }}
          >
            {user?.email?.charAt(0).toUpperCase() || 'A'}
          </Avatar>
          {drawerOpen && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {user?.email?.split('@')[0] || 'Admin'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.6875rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {user?.email || 'admin@pcu.com'}
              </Typography>
            </Box>
          )}
        </Box>

        <Menu
          anchorEl={profileMenuAnchor}
          open={Boolean(profileMenuAnchor)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: isRTL ? 'left' : 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' }}
          PaperProps={{
            elevation: 4,
            sx: {
              minWidth: 220,
              mt: -0.5,
              borderRadius: 2,
              overflow: 'hidden',
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '1rem' }}>
                {user?.email?.charAt(0).toUpperCase() || 'A'}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={600} noWrap>
                  {user?.email?.split('@')[0] || 'Admin'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {user?.email || 'admin@pcu.com'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          <MenuItem onClick={handleProfileSettings} sx={{ py: 1.25, gap: 1 }}>
            <ListItemIcon><Person fontSize="small" /></ListItemIcon>
            <ListItemText>{t('profileSettings')}</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleAdminSettings} sx={{ py: 1.25, gap: 1 }}>
            <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
            <ListItemText>{t('adminSettings')}</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleSignOut} sx={{ py: 1.25, gap: 1, color: 'error.main' }}>
            <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>{t('signOut')}</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  const currentDrawerWidth = drawerOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED;

  return (
    <Box sx={{ display: 'flex', direction: isRTL ? 'rtl' : 'ltr', minHeight: '100vh' }}>

      {/* ── AppBar ────────────────────────────────────────────── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: isRTL ? 0 : `${currentDrawerWidth}px` },
          mr: { sm: isRTL ? `${currentDrawerWidth}px` : 0 },
          bgcolor: 'primary.main',
          color: 'white',
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: `${TOPBAR_HEIGHT}px !important` }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            size="small"
            sx={{ me: 1.5, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            sx={{ flexGrow: 1, fontSize: '0.9375rem', letterSpacing: '-0.01em' }}
          >
            {location.pathname === '/'
              ? t('dashboard')
              : menuSections.flatMap(s => s.items).find(i => i.path === location.pathname)?.label || t('appName')}
          </Typography>

          <Tooltip title={t('changeLanguage') || 'Change Language'}>
            <IconButton color="inherit" onClick={toggleLanguage} size="small">
              <Language sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* ── Sidebar ───────────────────────────────────────────── */}
      <Box component="nav" sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          anchor={isRTL ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderInlineEnd: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          anchor={isRTL ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              borderInlineEnd: `1px solid ${theme.palette.divider}`,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* ── Main content ──────────────────────────────────────── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2.5,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          minHeight: '100vh',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box sx={{ height: TOPBAR_HEIGHT }} />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
